import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import Stack from "@mui/material/Stack";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { useMobileLocale } from "mobile/i18n";

import logo from "assets/images/logos/ShortLogo.png";

const COLORS = {
  ink: "#0b1118",
  text: "#17202a",
  muted: "#667085",
  line: "rgba(11,17,24,0.1)",
  soft: "#f6f7f9",
  red: "#cf241f",
  green: "#0f766e",
};

const roleConfig = {
  install: { label: "Installer", title: "Installer access", copy: "Create or access an account for unit registration, service records, and support.", icon: "build_circle", accountType: "customer", next: "/m" },
  sell: { label: "Seller", title: "Seller access", copy: "Create or access a distributor account for catalog review, pricing, and order activity.", icon: "storefront", accountType: "distributor", next: "/m" },
};

function normalizeMode(value) {
  const key = String(value || "").trim().toLowerCase();
  if (key === "seller" || key === "sell" || key === "distributor") return "sell";
  if (key === "installing" || key === "install" || key === "installer") return "install";
  return "";
}

export default function MobileQrDeal() {
  const navigate = useNavigate();
  const { t } = useMobileLocale();
  const [searchParams] = useSearchParams();
  const initialMode = normalizeMode(searchParams.get("mode"));
  const [mode, setMode] = useState(initialMode || "install");
  const roles = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(roleConfig).map(([key, item]) => [
          key,
          { ...item, label: t(item.label), title: t(item.title), copy: t(item.copy) },
        ])
      ),
    [t]
  );
  const selected = roles[mode] || roles.install;

  const registerPath = useMemo(() => {
    const params = new URLSearchParams({
      accountType: selected.accountType,
      next: selected.next,
      source: "qr",
    });
    return `/m/register?${params.toString()}`;
  }, [selected]);

  const signInPath = useMemo(() => {
    const params = new URLSearchParams({ next: selected.next, source: "qr" });
    return `/m/authentication/sign-in?${params.toString()}`;
  }, [selected]);

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#fbfbfa", color: COLORS.ink }}>
      <Container maxWidth="sm" sx={{ px: 2, pt: 2, pb: 5 }}>
        <MDBox display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 3.5 }}>
          <MDBox
            component="img"
            src={logo}
            alt="vbspecialties.com"
            sx={{ width: 82, height: "auto", objectFit: "contain", display: "block" }}
          />
          <MDButton
            variant="text"
            sx={{ color: COLORS.ink, fontWeight: 900, minWidth: "auto", px: 0.5 }}
            onClick={() => navigate("/m")}
          >
            {t("Home")}
          </MDButton>
        </MDBox>

        <MDTypography
          variant="h1"
          sx={{
            color: COLORS.ink,
            fontWeight: 900,
            fontSize: "clamp(2rem, 8vw, 2.7rem)",
            lineHeight: 1.02,
            letterSpacing: 0,
            mb: 2.25,
          }}
        >
          {t("Choose access.")}
        </MDTypography>

        <Grid container spacing={0.75} sx={{ mb: 3 }}>
          {Object.entries(roles).map(([key, item]) => {
            const active = key === mode;
            return (
              <Grid item xs={6} key={key}>
                <MDButton
                  fullWidth
                  variant={active ? "contained" : "outlined"}
                  sx={{
                    bgcolor: active ? COLORS.ink : "#fff",
                    color: active ? "#fff" : COLORS.ink,
                    borderColor: active ? COLORS.ink : "rgba(11,17,24,0.12)",
                    borderRadius: 1,
                    py: 1.2,
                    fontWeight: 900,
                    boxShadow: "none",
                    "&:hover": {
                      bgcolor: active ? COLORS.ink : "#fff",
                      borderColor: active ? COLORS.ink : "rgba(207,36,31,0.35)",
                      boxShadow: "none",
                    },
                  }}
                  onClick={() => setMode(key)}
                >
                  {item.label}
                </MDButton>
              </Grid>
            );
          })}
        </Grid>

        <MDBox sx={{ color: COLORS.text, mb: 2 }}>
          <MDBox
            sx={{
              width: 48,
              height: 48,
              borderRadius: 1,
              bgcolor: mode === "sell" ? "rgba(207,36,31,0.1)" : "rgba(15,118,110,0.1)",
              color: mode === "sell" ? COLORS.red : COLORS.green,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 1.5,
            }}
          >
            <Icon>{selected.icon}</Icon>
          </MDBox>
          <MDTypography variant="h4" sx={{ color: COLORS.ink, fontWeight: 900, letterSpacing: 0, mb: 1 }}>
            {selected.title}
          </MDTypography>
          <MDTypography variant="body2" sx={{ color: COLORS.muted, lineHeight: 1.65, mb: 2.25 }}>
            {selected.copy}
          </MDTypography>

          <Stack spacing={1.15}>
            <MDButton
              fullWidth
              variant="contained"
              sx={{
                bgcolor: COLORS.red,
                color: "#fff",
                borderRadius: 1,
                py: 1.35,
                fontWeight: 900,
                boxShadow: "none",
                "&:hover": { bgcolor: "#af1f1a", boxShadow: "none" },
              }}
              onClick={() => navigate(registerPath)}
            >
              {t("Create account")}
            </MDButton>
            <MDButton
              fullWidth
              variant="outlined"
              sx={{ borderColor: COLORS.line, color: COLORS.ink, borderRadius: 1, py: 1.35, fontWeight: 900 }}
              onClick={() => navigate(signInPath)}
            >
              {t("Sign in")}
            </MDButton>
          </Stack>
        </MDBox>

        <MDButton
          fullWidth
          variant="text"
          sx={{ color: COLORS.ink, fontWeight: 900 }}
          onClick={() => navigate("/m/catalog")}
        >
          {t("View catalog")}
        </MDButton>
      </Container>
    </Box>
  );
}
